/** Minimal shape of a deadline tag, structurally compatible with `SyntheticTag`. */
export type DeadlineTag = {
  id: string;
  label: string;
  origin: string;
};

/** Tag edits to persist for one synthetic deadline: labels to add, tag ids to remove. */
export type DeadlineTagChanges = {
  add: string[];
  remove: string[];
};

/**
 * Diff a staged tag list against the deadline's saved tags.
 *
 * Labels are the identity here (a synthetic can hold each label at most once),
 * so a staged tag needs no id until it is persisted. AI tags are never removed:
 * the API only deletes manual ones, and the UI renders them read-only.
 */
export function diffDeadlineTags(
  saved: DeadlineTag[],
  staged: DeadlineTag[],
): DeadlineTagChanges {
  const savedLabels = new Set(saved.map((tag) => tag.label));
  const stagedLabels = new Set(staged.map((tag) => tag.label));

  return {
    add: staged.filter((tag) => !savedLabels.has(tag.label)).map((tag) => tag.label),
    remove: saved
      .filter((tag) => tag.origin === 'manual' && !stagedLabels.has(tag.label))
      .map((tag) => tag.id),
  };
}

export function hasDeadlineTagChanges(changes: DeadlineTagChanges): boolean {
  return changes.add.length > 0 || changes.remove.length > 0;
}
